import React from 'react';
import StatusPill from '../StatusPill';
import { fmt, fmtCompact, fmtPercent } from '../format';
import { cardStyle } from '../styles';
import { niceScale } from './chartUtils';

const WIDTH = 460;
const HEIGHT = 300;
const MARGIN = { left: 64, right: 16, top: 16, bottom: 30 };
const PLOT_WIDTH = WIDTH - MARGIN.left - MARGIN.right;
const PLOT_HEIGHT = HEIGHT - MARGIN.top - MARGIN.bottom;

const BUDGET_COLOR = '#9aa0a6';
const RESULT_COLOR = '#1779ba';
const LAST_YEAR_COLOR = '#e0892b';

// Cumulative values starting at 0 before the first month
const cumulative = (values) => values.reduce((acc, value) => [...acc, acc[acc.length - 1] + value], [0]);

// Position of today within the target year as a 0..1 fraction (1 once the year is over)
const getTodayFraction = (now, year) => {
    if (now.getFullYear() > year) return 1;
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return (now.getMonth() + now.getDate() / daysInMonth) / 12;
};

const BudgetChart = ({ title, budget, lastYear, result, lastMonth, year }) => {
    const now = new Date();
    const budgetTotal = cumulative(budget);
    const lastYearTotal = cumulative(lastYear);
    const resultTotal = cumulative(lastMonth >= 0 ? result.slice(0, lastMonth + 1) : []);
    const resultToDate = resultTotal[resultTotal.length - 1];
    const scale = niceScale(Math.max(budgetTotal[12], lastYearTotal[12], resultToDate));

    const x = (fraction) => MARGIN.left + PLOT_WIDTH * fraction;
    const y = (value) => MARGIN.top + PLOT_HEIGHT * (1 - value / scale.max);
    const toPath = (points) => points.map(([fx, value], i) => `${i === 0 ? 'M' : 'L'}${x(fx).toFixed(1)} ${y(value).toFixed(1)}`).join(' ');

    const budgetPoints = budgetTotal.map((value, i) => [i / 12, value]);
    const lastYearPoints = lastYearTotal.map((value, i) => [i / 12, value]);

    // The latest result point is placed at today's date, earlier points at month ends
    const resultPoints = lastMonth >= 0
        ? resultTotal.map((value, i) => [i === resultTotal.length - 1 ? getTodayFraction(now, year) : i / 12, value])
        : [];
    const lastResultPoint = resultPoints[resultPoints.length - 1];

    const budgetToDate = budgetTotal[lastMonth + 1] ?? 0;
    const ratio = budgetToDate > 0 ? resultToDate / budgetToDate : 0;

    const xTicks = [[0, '1 jan'], [0.25, 'Apr'], [0.5, 'Jul'], [0.75, 'Okt'], [1, '31 dec']];

    return (
        <div style={{ ...cardStyle, padding: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.5rem' }}>
                <strong>{title}</strong>
                {lastMonth >= 0 && <StatusPill ratio={ratio}>{fmtPercent(ratio)}</StatusPill>}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#666', margin: '0.25rem 0 0.5rem' }}>
                <span style={{ color: RESULT_COLOR }}>&#9632;</span> Utfall {fmt(resultToDate)}
                <span style={{ color: BUDGET_COLOR, marginLeft: '1rem' }}>&#9632;</span> Budget {year} {fmt(budgetTotal[12])}
                <span style={{ color: LAST_YEAR_COLOR, marginLeft: '1rem' }}>&#9632;</span> {year - 1} {fmt(lastYearTotal[12])}
            </div>

            <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} style={{ width: '100%', height: 'auto' }} role="img" aria-label={`Utfall mot budget f\u00f6r ${title}`}>
                {scale.ticks.map((value) => (
                    <g key={value}>
                        <line x1={MARGIN.left} x2={WIDTH - MARGIN.right} y1={y(value)} y2={y(value)} stroke="#eceef1" />
                        <text x={MARGIN.left - 8} y={y(value) + 4} textAnchor="end" fontSize="11" fill="#777">{fmtCompact(value)}</text>
                    </g>
                ))}

                {xTicks.map(([fraction, label]) => (
                    <text
                        key={label}
                        x={x(fraction)}
                        y={HEIGHT - 8}
                        textAnchor={fraction === 0 ? 'start' : fraction === 1 ? 'end' : 'middle'}
                        fontSize="11"
                        fill="#777"
                    >
                        {label}
                    </text>
                ))}

                <path d={toPath(lastYearPoints)} fill="none" stroke={LAST_YEAR_COLOR} strokeWidth="2" strokeDasharray="2 3" />
                <path d={toPath(budgetPoints)} fill="none" stroke={BUDGET_COLOR} strokeWidth="2" strokeDasharray="6 4" />
                {resultPoints.length > 1 && <path d={toPath(resultPoints)} fill="none" stroke={RESULT_COLOR} strokeWidth="2.5" strokeLinejoin="round" />}
                {lastResultPoint && (
                    <circle cx={x(lastResultPoint[0])} cy={y(lastResultPoint[1])} r="4" fill={RESULT_COLOR}>
                        <title>{`Utfall: ${fmt(lastResultPoint[1])}`}</title>
                    </circle>
                )}
            </svg>
        </div>
    );
};

export default BudgetChart;
